import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@klickdata/core/auth';
import { Customer } from '@klickdata/core/customer';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { AppScope, OccasionsDates, Resource, ResourceData, ResourceService } from '@klickdata/core/resource';
import { OccasionDate } from '@klickdata/core/resource/src/resource.model';
import {
    Filter,
    GlobalFilterProperty,
    GlobalFilterPropertyType,
    SelectFilterOption,
    TableFilterComponent,
} from '@klickdata/core/table';
import { User, UserActionHistory } from '@klickdata/core/user';
import { ResourceApprovalsListingService } from '@klickdata/core/user-messages/src/user-messages/user-messages-center/user-messages-approvals/resource-approvals-listing.service';
import { Utils } from '@klickdata/core/util';
import { ShareService } from '@klickdata/shared-components/src/share/share.service';
import { UserTableBaseComponent } from '@klickdata/shared-components/src/user-table-base/user-table-base.component';
import { DownloadHelperService } from 'apps/klickdata/src/app/shared/dialog/download-pdf-dialog/download-helper.service';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import * as moment from 'moment';
import { filter, map, Observable, switchMap, takeUntil } from 'rxjs';
import { LocalStorageService } from 'ngx-webstorage';

@Component({
    selector: 'app-instructor-enrollments-users-listing',
    templateUrl: './instructor-enrollments-users-listing.component.html',
    styleUrls: ['./instructor-enrollments-users-listing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ResourceApprovalsListingService],
})
export class InstructorEnrollmentsUsersListingComponent extends OnDestroyHandler implements AfterViewInit, OnInit {
    @ViewChild('tableBase') tableBase: UserTableBaseComponent;
    @Input() authUser: User;
    @Input() tableFilterItems: Filter<any>[];
    @Input() approvalStatus: string;
    @Input() resource: Resource;
    @Input() occasion: OccasionsDates;
    @Input() allOccasions: OccasionsDates[];
    @Output() onUpdateStatus: EventEmitter<void> = new EventEmitter<void>();
    @ViewChild('name', { static: true }) name: TemplateRef<any>;
    @ViewChild('img', { static: true }) img: TemplateRef<any>;
    @ViewChild('registred_date', { static: true }) registred_date: TemplateRef<any>;
    @ViewChild('registration_status', { static: true }) registration_status: TemplateRef<any>;
    @ViewChild('competence_status', { static: true }) competence_status: TemplateRef<any>;
    @ViewChild('tools', { static: true }) tools: TemplateRef<any>;
    @ViewChild('status_form', { static: true }) statusForm: TemplateRef<any>;
    @ViewChild('change_occasion', { static: true }) changeOccasionControlRef: TemplateRef<any>;
    @ViewChild(TableFilterComponent) filter: TableFilterComponent;
    public changeOccasionControl = new FormControl();
    GlobalFilterPropertyType = GlobalFilterPropertyType;
    public isMobile$: Observable<boolean>;
    public customer$: Observable<Customer>;
    public expandedRowId: number | null;
    GlobalFilterProperty = GlobalFilterProperty;
    columnsData: { [key: string]: TemplateRef<any> } = {};
    otherContext: { [key: string]: TemplateRef<any> } = {};
    public approvalStatusOptions: SelectFilterOption[];
    columnsKeys: any = {
        name: { label: $localize`Name`, sortable: true },
        img: { label: '', sortable: true },
        registred_date: { label: $localize`Enrollment Date`, sortable: true, icon: 'edit_calendar' },
        registration_status: { label: $localize`Decision`, sortable: true, icon: 'assignment_turned_in' },
        competence_status: { label: $localize`Competence`, sortable: true, icon: 'design_services' },
    };
    statusFormGroup: FormGroup;
    AppScope = AppScope;
    Utils = Utils;
    expandedFn = (user: User) => (user.opportunity_id || user.id) === this.expandedRowId;
    private storage = inject(LocalStorageService);

    constructor(
        protected downloadHelper: DownloadHelperService,
        public shareService: ShareService,
        protected router: Router,
        protected route: ActivatedRoute,
        protected cdr: ChangeDetectorRef,
        protected mobile: MobileService,
        protected resourceService: ResourceService,
        protected auth: AuthService,
        protected messageService: MessageService,
        protected resourceEnrollmentsListingService: ResourceApprovalsListingService,
        private formBuilder: FormBuilder
    ) {
        super();
        this.isMobile$ = mobile.isMobile();
        this.statusFormGroup = this.formBuilder.group({
            status: [null],
            comment: [null],
        });
        this.approvalStatusOptions = Utils.getResApprovalStatusOptions();
    }
    ngOnInit(): void {
        this.route.queryParams
            .pipe(
                filter((params) => params.expand),
                takeUntil(this.destroy)
            )
            .subscribe((params) => {
                setTimeout(() => {
                    this.expandedRowId = +params.expand;
                    this.cdr.markForCheck();
                }, 500);
            });
        this.customer$ = this.auth.getCustomer();
        setTimeout(() => {
            if (this.approvalStatus) {
                this.filter.setActiveFilterWithSelectedOption(GlobalFilterProperty.REVIEW_STATUS, this.approvalStatus);
            } else {
                this.filter.clear();
            }
        }, 100);
        this.changeOccasionControl.setValue(this.occasion?.id);
    }
    get resourceOccasionDates(): OccasionDate[] {
        return this.resource?.user_collaboration?.enroll_approval_required?.metadata?.occasion_dates;
    }
    ngAfterViewInit(): void {
        this.columnsData['img'] = this.img;
        this.columnsData['name'] = this.name;
        this.columnsData['competence_status'] = this.competence_status;
        this.columnsData['registred_date'] = this.registred_date;
        this.columnsData['registration_status'] = this.registration_status;
        this.columnsData['tools'] = this.tools;
    }

    browse(user: User) {
        this.router.navigate(['/admin/accounts/users/users/' + user.id + '/actions']);
    }
    public sendMsg(user: User) {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.SEND_MESSAGE,
            data: { model: user, isUser: true },
        });
    }

    onSelectStats(user: User) {
        this.router.navigate(['/admin/statistics/course-results/users'], {
            queryParams: {
                user_id: user.id,
            },
        });
    }

    sendEmail(user: User) {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.SEND_MAIL,
            data: user,
        });
    }
    onMessageSelected() {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.SEND_MESSAGE,
            data: { models: this.tableBase.dataSource.data, isMultipleUsers: true },
        });
    }
    onEmailSelected() {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.EMAIL_MULTIPLE_USERS,
            data: {
                type: 'emailMultipleUsers',
                users: this.tableBase.dataSource.data,
            },
        });
    }
    changeOccasion(user: User) {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'change_circle',
                title: $localize`Change occasion`,
                contentBody: $localize`You are about to change occasion of ${user.name}.`,
                positiveBtn: $localize`Done`,
                injectedTemplate: this.changeOccasionControlRef,
                negativeBtn: $localize`Cancel`,
            },
        });
        this.mobile
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                filter(() => this.changeOccasionControl.value !== this.occasion?.id),
                switchMap(() =>
                    this.resourceService.changeOccasion(
                        [user.enrollment.collaboration_id],
                        this.changeOccasionControl.value
                    )
                )
            )
            .subscribe((res) => {
                this.storage.clear(UserActionHistory.SELECTED_HOME_TABS_RESOURCE_OCCASION_ID);
                this.messageService.openMessage(MessageSavedComponent, $localize`Occasion changed successfully.`);
                this.onUpdateStatus.emit();
            });
    }
    onChngeStatus(user: User, status: 'approved' | 'rejected' | 'pending' | 'waiting', needSlideIn: boolean = true) {
        if (!needSlideIn) {
            this.resourceEnrollmentsListingService
                .reviewCollRequest(
                    status === 'waiting' ? 'enroll_requested' : 'enroll_approved',
                    user.enrollment.collaboration_id,
                    status === 'approved' ? true : status === 'rejected' ? false : null,
                    null
                )
                .pipe(takeUntil(this.destroy))
                .subscribe(() => {
                    user.enrollment.approval_status =
                        status === 'approved' ? 'approved' : status === 'rejected' ? 'rejected' : 'waiting';
                    this.onChangeStatus();
                });
        } else {
            user.enrollment.applicant.media = user.media;
            this.mobile.updateSideNavSub({
                dataType: SideNaveDataTypes.RESOURCE_ENROLLMENT_APPROVAL,
                data: {
                    icon:
                        status === 'approved'
                            ? 'check_circle'
                            : status === 'rejected'
                            ? 'cancel'
                            : status === 'waiting'
                            ? 'pending'
                            : 'pending_actions',
                    title: $localize`Update enrollment status`,
                    resource: this.resource,
                    enrollment: user.enrollment,
                    applicantMsg: user.enrollment?.applicant?.message?.body,
                    noAvailableSeats: status === 'approved' || (!this.occasion && !this.resource?.hasAvailableSeats),
                    type: 'enrollApproval',
                },
            });
            this.mobile
                .getSideNavResponseData()
                .pipe(
                    takeUntil(this.destroy),
                    filter((data) => data.type == 'enrollApproval'),
                    switchMap((data) =>
                        this.resourceEnrollmentsListingService
                            .reviewCollRequest(
                                data.approved === null ? 'enroll_requested' : 'enroll_approved',
                                user.enrollment.collaboration_id,
                                data.approved,
                                data.message
                            )
                            .pipe(map(() => data.approved))
                    )
                )
                .subscribe((approved) => {
                    user.enrollment.approval_status = approved;
                    this.onChangeStatus();
                });
        }
    }
    private onChangeStatus() {
        this.cdr.markForCheck();
        this.messageService.openMessage(MessageSavedComponent, $localize`Enrollment status updated`);
        this.onUpdateStatus.emit();
    }

    expansionToggled(user: User) {
        this.expandedRowId = this.expandedRowId === user.id ? null : user.id;
    }

    public getCircleMetadata(userCompetence: ResourceData): { value: any; class: string; tooltip: any } {
        const isValidDate =
            userCompetence && userCompetence?.done_at && moment(userCompetence?.done_at).year() !== 1970;

        return userCompetence
            ? {
                  value: userCompetence.competence_label,
                  class: userCompetence.competence_status,
                  tooltip: isValidDate ? moment(userCompetence.done_at).format('YYYY-MM-DD') : '',
              }
            : {
                  value: '',
                  class: 'grey',
                  tooltip: $localize`The competence is not mandatory and has not been taken yet.`,
              };
    }
}
